import { screenDpr, tierIndex } from '../graphics';

/**
 * The widest zoom step per graphics tier (lane ZOOM-OUT, request 002). A smaller step shows more ground but
 * the world picture grows with the square of 1/step, so the low tiers stop wider than the high ones. The old
 * single floor was 1.6.
 */
const FLOOR: readonly number[] = [1.4, 1.2, 1, 1, 1];

/** Dev only: `window.__zoomFloor` (a number) overrides the floor, for the lane's perf and still runs. */
const devFloor = (): number | null => {
  if (!import.meta.env.DEV) return null;
  const v = (globalThis as { __zoomFloor?: unknown }).__zoomFloor;
  return typeof v === 'number' && v > 0 ? v : null;
};

/** renderer.ts PIXEL_SIZE: an art pixel is the zoom step times this many CSS pixels. */
const PIXEL_SIZE = 1.25;
/** The old floor: 2 CSS pixels an art pixel, whole on every canvas scale the tiers use (1, 1.5, 2, 3). */
const SAFE = 1.6;

/**
 * The floor snapped to a clean pixel ratio (lane ZOOM-OUT, techart review): the closest-in step at or past the
 * tier's wanted floor that puts a whole number of canvas pixels on every art pixel. The view samples the world
 * picture nearest, so at 1.4 on a 1x canvas an art pixel is 1.75 pixels (2,2,2,1) and 1-px outlines and dithers
 * crawl as the camera moves. On a 1x canvas (tiers 0-1, any tier on a 1x screen) the next clean step past 1.6
 * is 0.8, which drops tier 0 to 38 fps, so those stay at 1.6; on a 2x canvas (tiers 3-4 on a Retina screen) it
 * is 1.2 (3 pixels an art pixel), on a 1.5x canvas (tier 2) 1.0667 (2 pixels).
 */
export const cleanFloor = (want: number, dpr: number): number => {
  const unit = PIXEL_SIZE * dpr;
  const n = Math.ceil(want * unit - 1e-6);
  return Math.min(SAFE, n / unit);
};

export const tierFloorPx = (): number => devFloor() ?? cleanFloor(FLOOR[tierIndex()] ?? SAFE, screenDpr());

let drop: number | null = null;
/** A bout lowers the floor while it frames both fighters (render/duelFraming.ts); null puts it back. */
export const setFloorDrop = (v: number | null): void => {
  drop = v;
};

export const minZoomPx = (): number => (drop === null ? tierFloorPx() : Math.min(tierFloorPx(), drop));

/**
 * The floor for a bout (DUEL-RING framing): the next clean step wider than the tier's floor, but only when it is no
 * wider than 0.8 of it (what the perf allows); otherwise null, and the bout keeps the tier's floor (the HUD compacts
 * instead). A plain 0.8 drop gave 1.28 on a 1x screen (1.6 px an art pixel) and crawling outlines in every duel. With
 * the 1.25 art pixel the next clean step is always 0.5–0.75 of the floor on today's canvases, so bouts do not drop.
 */
export const boutFloorPx = (): number | null => {
  const floor = tierFloorPx();
  const unit = PIXEL_SIZE * screenDpr();
  const n = Math.round(floor * unit) - 1;
  if (n < 1 || Math.abs(floor * unit - Math.round(floor * unit)) > 1e-6) return null;
  const next = n / unit;
  return next >= floor * 0.8 - 1e-6 ? next : null;
};
